import React, { createContext, useCallback, useState } from 'react';
import {
	type CarouselConsumerProps,
	type CarouselContextValues,
	type CarouselProps,
	type ItemPosition,
} from './types';
import { Control } from './carousel-control';
import { Item } from './carousel-item';
import { Slide } from './carousel-slide';

export const defaultCarouselContextValues = {
	visiblePosition: 1,
	onNext: () => void 0,
	onPrevious: () => void 0,
	onChangePosition: () => void 0,
	isStart: true,
	isEnd: false,
};
export const CarouselContext = createContext<CarouselContextValues>(defaultCarouselContextValues);

export const CarouselConsumer = ({ children }: CarouselConsumerProps) => (
	<CarouselContext.Consumer>
		{(context) => {
			if (!context) {
				throw new Error('Please pass the necessary context');
			}
			if (!children) {
				throw new Error('Please add at least one child component');
			}
			return children(context);
		}}
	</CarouselContext.Consumer>
);

export const Carousel = (props: CarouselProps) => {
	const { slideRange, children, loop } = props;
	const [slideStart, slideEnd] = slideRange;
	const [visiblePosition, setVisiblePosition] = useState(slideStart);

	const isFirstItemVisible = visiblePosition === slideStart;
	const isLastItemVisible = visiblePosition === slideEnd;

	const handleChangePosition = (position: ItemPosition) => {
		setVisiblePosition(position);
	};

	const showNextItem = useCallback(() => {
		if (isLastItemVisible && !loop) {
			return;
		}
		const shouldResetToStart = isLastItemVisible && loop;
		const nextItemPosition = visiblePosition + 1;
		const newPosition = shouldResetToStart ? slideStart : nextItemPosition;
		handleChangePosition(newPosition);
	}, [isLastItemVisible, loop, slideStart, visiblePosition]);

	const showPreviousItem = useCallback(() => {
		if (isFirstItemVisible && !loop) {
			return;
		}
		const shouldJumpToEnd = loop && isFirstItemVisible;
		const previousItemPosition = visiblePosition - 1;
		const newPosition = shouldJumpToEnd ? slideEnd : previousItemPosition;
		handleChangePosition(newPosition);
	}, [isFirstItemVisible, loop, slideEnd, visiblePosition]);

	return (
		<CarouselContext.Provider
			value={{
				visiblePosition,
				onNext: showNextItem,
				onPrevious: showPreviousItem,
				onChangePosition: handleChangePosition,
				isStart: isFirstItemVisible,
				isEnd: isLastItemVisible,
			}}
		>
			{children}
		</CarouselContext.Provider>
	);
};

Carousel.Control = Control;
Carousel.Item = Item;
Carousel.Slide = Slide;

export default Carousel;
